/**
 * Brand profile registry — drives the white-label demo mode for CEO pitches.
 *
 * The theme COLOURS are handled separately via ThemeToggle.jsx / CSS variables
 * on <html data-theme="...">. This module adds the WORDMARK / dealer name layer
 * on top of that, so when we demo to a prospect we can put their actual brand
 * in front of them.
 *
 * Read order:
 *   1. ?brand=rrg / ?brand=toyota query param (override)
 *   2. localStorage["ssp_brand_profile"]
 *   3. "default" (ServiceSell PRO)
 *
 * Setting ?brand=default clears the override.
 */

export const BRAND_PROFILES = {
  default: {
    key: "default",
    dealerName: "ServiceSell Pro",
    wordmark: "ServiceSell PRO",
    productName: "ServiceSell PRO",
    tagline: "Inspect. Advise. Sell. Grow.",
    theme: "ssp",      // matches existing ThemeToggle theme IDs
    logoText: null,    // null = use existing image logo
    logoTextColor: "#D2E031",
    badge: null,       // no demo-mode pill
  },
  rrg: {
    key: "rrg",
    dealerName: "RRG Group",
    wordmark: "RRG",
    productName: "RRG Smart Service",
    tagline: "Inspect. Advise. Sell. Grow.",
    theme: "rrg",
    logoText: "RRG",
    logoTextColor: "#22D3D9",  // RRG cyan
    // Real RRG Group logo (cyan border, dark slate body, white "RRG" wordmark
    // with car silhouette above). Hosted as a job asset.
    logoUrl: "https://customer-assets.emergentagent.com/job_mechanic-video-shop/artifacts/rtz7gf43_IMG_5174.jpeg",
    badge: "DEMO MODE — RRG GROUP",
  },
  toyota: {
    key: "toyota",
    dealerName: "Toyota",
    wordmark: "TOYOTA",
    productName: "Toyota Smart Service",
    tagline: "Inspect. Advise. Sell. Grow.",
    theme: "toyota",
    logoText: "TOYOTA",
    logoTextColor: "#EB0A1E",
    badge: "DEMO MODE — TOYOTA",
  },
  // ENSO Automotive — Mikey Durham's independent supercar workshop.
  // Auto-applied on login by AuthContext when workshop_name matches, so
  // Mikey lands on his own Ferrari-red palette without needing to press
  // the Magic Brand Switch. Not part of Steve's Toyota/RRG pitch cycle.
  enso: {
    key: "enso",
    dealerName: "ENSO Automotive",
    wordmark: "ENSO",
    productName: "ENSO Automotive",
    tagline: "Precision · Provenance · Peace of mind.",
    theme: "enso",
    logoText: "ENSO",
    logoTextColor: "#FF2800",  // Ferrari Rosso Corsa
    badge: null,               // No demo pill — this is Mikey's real branding
  },
  // Hyundai Smart Service — clean demo tenant for Hyundai dealer pitches.
  // Auto-applied on login by AuthContext when workshop_name matches.
  hyundai: {
    key: "hyundai",
    dealerName: "Hyundai",
    wordmark: "HYUNDAI",
    productName: "Hyundai Smart Service",
    tagline: "Inspect. Advise. Sell. Grow.",
    theme: "hyundai",
    logoText: "HYUNDAI",
    logoTextColor: "#00AAD2",  // Hyundai Sky Blue
    badge: "DEMO MODE — HYUNDAI",
  },
  // Geely Smart Service — black & silver demo tenant for the Geely Auto UK
  // pitch (aftersales + network development team). Auto-applied on login.
  geely: {
    key: "geely",
    dealerName: "Geely",
    wordmark: "GEELY",
    productName: "Geely Smart Service",
    tagline: "Inspect. Advise. Sell. Grow.",
    theme: "geely",
    logoText: "GEELY",
    logoTextColor: "#C7CCD4",  // brushed silver
    badge: "DEMO MODE — GEELY",
  },
  // Omoda & Jaecoo Smart Service — Chery UK pitch tenant (James Dai, Aftersales
  // Director). Omoda teal on near-black. Auto-applied on login.
  omoda: {
    key: "omoda",
    dealerName: "Omoda & Jaecoo",
    wordmark: "OMODA & JAECOO",
    productName: "Omoda & Jaecoo Smart Service",
    tagline: "Inspect. Advise. Sell. Grow.",
    theme: "omoda",
    logoText: "OMODA & JAECOO",
    logoTextColor: "#00B5AD",
    badge: "DEMO MODE — OMODA & JAECOO",
  },
  // Vehicle Visuals showcase tenant — ServiceSell PRO demo dealership for
  // Phillip Woodman to show his dealer contacts. VV blue on graphite.
  vv: {
    key: "vv",
    dealerName: "Vehicle Visuals",
    wordmark: "VEHICLE VISUALS",
    productName: "ServiceSell PRO × Vehicle Visuals",
    tagline: "Talk less. Sell more.",
    theme: "vv",
    logoText: "VV",
    logoTextColor: "#7F95E8",
    badge: "SHOWCASE — VEHICLE VISUALS",
  },
};

const STORAGE_KEY = "ssp_brand_profile";

export function getBrandFromUrl() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const b = params.get("brand");
  if (b && BRAND_PROFILES[b]) return b;
  return null;
}

export function getActiveBrandKey() {
  // URL param wins over storage
  const fromUrl = getBrandFromUrl();
  if (fromUrl) return fromUrl;
  try {
    const fromStorage = localStorage.getItem(STORAGE_KEY);
    if (fromStorage && BRAND_PROFILES[fromStorage]) return fromStorage;
  } catch { /* SSR / private mode */ }
  return "default";
}

export function getActiveBrand() {
  return BRAND_PROFILES[getActiveBrandKey()] || BRAND_PROFILES.default;
}

export function setActiveBrand(key) {
  if (!BRAND_PROFILES[key]) return;
  try { localStorage.setItem(STORAGE_KEY, key); } catch { /* ignore */ }
  // Apply the colour theme too — keeps the two in lock-step
  const profile = BRAND_PROFILES[key];
  const root = document.documentElement;
  if (profile.theme === "ssp") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", profile.theme);
  // Keep ThemeToggle's cache in lock-step so a stale palette (e.g. ENSO red)
  // can't repaint on the next reload before the server hydrates.
  try { localStorage.setItem("ssp_brand_theme", profile.theme); } catch { /* ignore */ }
}

/**
 * Initialise brand on app boot. Reads ?brand= URL param, persists to
 * localStorage, and applies the matching colour theme to <html>. Call once,
 * early in App.js — same pattern as initBrandTheme().
 */
export function initBrandProfile() {
  const fromUrl = getBrandFromUrl();
  if (fromUrl) {
    setActiveBrand(fromUrl);
  } else {
    // Just apply whatever's in storage (no-op for default)
    const key = getActiveBrandKey();
    if (key !== "default") setActiveBrand(key);
  }
}
